import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow,
} from '@carbon/react';
import dayjs from 'dayjs';
import { useAuth } from '../auth/AuthContext';
import api from '../api';

// 処方薬のID→表示テキスト（ID/テキスト両対応）
const DRUG_LABELS = {
  ofatumumab: 'ケシンプタ（オファツムマブ）',
  siponimod: 'メーゼント（シポニモド）',
  dmf: 'テクフィデラ（フマル酸ジメチル）',
  natalizumab: 'タイサブリ（ナタリズマブ）',
  fingolimod: 'イムセラ・ジレニア（フィンゴリモド）',
  glatiramer: 'コバキソン（グラチラマー酢酸塩）',
  ifn_beta1a: 'アボネックス（IFNβ-1a）',
  ifn_beta1b: 'ベタフェロン（IFNβ-1b）',
  none: '無治療',
};

function fmtOneDecimal(val) {
  const n = typeof val === 'number' ? val : parseFloat(String(val ?? ''));
  return Number.isFinite(n) ? n.toFixed(1) : '-';
}

function getRecordDate(rec) {
  if (rec?.consultation_date) return dayjs(rec.consultation_date);
  if (rec?.date) return dayjs(rec.date);
  if (rec?.timestamp) return dayjs(rec.timestamp);
  return dayjs('');
}

function getDrugLabel(drug) {
  if (!drug) return '-';
  if (typeof drug === 'string') {
    return DRUG_LABELS[drug] ?? drug;
  }
  if (typeof drug === 'object') {
    const id = drug.id;
    const text = drug.text;
    return text ?? (DRUG_LABELS[id] ?? '-');
  }
  return '-';
}

export default function MedicalRecordScrollTable({ records = [] }) {
  const [openNoteIndex, setOpenNoteIndex] = useState(null);
  const [comment, setComment] = useState('');
  const [loadingNote, setLoadingNote] = useState(false);
  const { role, user } = useAuth() || {};
  const isPatient = role === 'patient';

  if (!records || records.length === 0) return <p>診察履歴データがありません。</p>;

  const dateHeaders = records.map((rec) => {
    const d = getRecordDate(rec);
    return d.isValid() ? d.format('YYYY/MM/DD') : '-';
  });

  // === 主治医コメント取得関数 ===
  const fetchDoctorComment = async (patientId, date) => {
    try {
      setLoadingNote(true);
      setComment('');

      // セッションストレージから患者情報を取得（患者選択済みの想定）
      const storedPatient = sessionStorage.getItem("patient");
      const patient = storedPatient ? JSON.parse(storedPatient) : null;
      const researchId =
        patientId ||
        patient?.research_id ||
        user?.id ||
        "P010"; // fallback（保険）

      const res = await api.post('/v1/doctor/doctor_comment', {
        research_id: researchId,
        consultation_date: date,
      });

      // 戻り値の形式が { doctor_comment: "string" } なので…
      setComment(res.data?.doctor_comment ?? '（メモなし）');
    } catch (err) {
      console.error('主治医コメント取得失敗:', err);
      setComment('（コメントの取得に失敗しました）');
    } finally {
      setLoadingNote(false);
    }
  };

  const baseRows = [
    {
      label: 'EDSSスコア',
      key: 'edss_score',
      render: (rec) => fmtOneDecimal(rec.edss_score ?? rec.score ?? rec.edss),
    },
    {
      label: '処方薬',
      key: 'drug_type',
      render: (rec) => getDrugLabel(rec.drug_type ?? rec.drug ?? rec.prescribedDrug),
    },
    {
      label: 'MRIに病変',
      key: 'mri_result',
      render: (rec) => rec.mri_result ?? '-',
    },
    {
      label: '再発の有無',
      key: 'recent_recurrence',
      render: (rec) => rec.recent_recurrence ?? '-',
    },
    {
      label: '医師メモ',
      key: 'note_button',
      render: (_rec, idx) => (
        <button
          onClick={() => {
            setOpenNoteIndex((prev) => (prev === idx ? null : idx));
            const rec = records[idx];
            if (rec && openNoteIndex !== idx) {
              const d = getRecordDate(rec);
              if (d.isValid()) {
                fetchDoctorComment(rec.research_id, d.format('YYYY-MM-DD'));
              }
            }
          }}
          aria-expanded={openNoteIndex === idx}
          style={{
            fontSize: '0.75rem',
            padding: '4px 8px',
            cursor: 'pointer',
            background: '#f4f4f4',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontWeight: 600,
          }}
        >
          {openNoteIndex === idx ? '閉じる' : '表示'}
        </button>
      ),
    },
  ];

  const rows = isPatient ? baseRows.filter((r) => r.key !== 'note_button') : baseRows;

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <TableContainer>
          <Table size="sm">
            <TableHead>
              <TableRow>
                <TableHeader>項目</TableHeader>
                {dateHeaders.map((date, idx) => (
                  <TableHeader key={idx}>{date}</TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.label}</TableCell>
                  {records.map((rec, j) => (
                    <TableCell key={j}>
                      {row.render ? row.render(rec, j) : rec[row.key] ?? '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {!isPatient && openNoteIndex !== null && (
        <div
          style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#f4f4f4',
            borderLeft: '4px solid #0f62fe',
          }}
        >
          <h5 style={{ marginTop: 0 }}>
            {dateHeaders[openNoteIndex]} の医師メモ
          </h5>
          {loadingNote ? (
            <p style={{ color: '#525252' }}>読み込み中...</p>
          ) : (
            <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
              {comment || '（メモなし）'}
            </p>
          )}
        </div>
      )}
    </>
  );
}
